/** Two inclusive ranges [aMin,aMax] and [bMin,bMax] overlap if they share any value. */
export function rangesOverlap(
  aMin: number,
  aMax: number,
  bMin: number,
  bMax: number
): boolean {
  return Math.max(aMin, bMin) <= Math.min(aMax, bMax);
}

/** Indices (i, j) with i < j where the two bands’ min/max ranges overlap. */
export function findOverlappingBandPairIndices(
  rows: { minValue: number; maxValue: number }[]
): [number, number][] {
  const pairs: [number, number][] = [];
  for (let i = 0; i < rows.length; i++) {
    for (let j = i + 1; j < rows.length; j++) {
      if (
        rangesOverlap(
          rows[i].minValue,
          rows[i].maxValue,
          rows[j].minValue,
          rows[j].maxValue
        )
      ) {
        pairs.push([i, j]);
      }
    }
  }
  return pairs;
}

/** True if any pair of bands has overlapping min/max ranges. */
export function hasAnyBandRangeOverlap(
  rows: { minValue: number; maxValue: number }[]
): boolean {
  return findOverlappingBandPairIndices(rows).length > 0;
}

/**
 * Original row indices sorted by min then max ascending — aligns calc API order with typical
 * “first matching band by ascending min” overlap rules.
 */
export function sortBandRowIndicesByBounds(
  rows: { minValue: number; maxValue: number }[]
): number[] {
  const n = rows.length;
  return [...Array(n).keys()].sort((a, b) => {
    const ra = rows[a];
    const rb = rows[b];
    if (ra.minValue !== rb.minValue) return ra.minValue - rb.minValue;
    if (ra.maxValue !== rb.maxValue) return ra.maxValue - rb.maxValue;
    return a - b;
  });
}

/** Band names must be non-empty and case-insensitively unique. */
export function bandNamesAreUnique(rows: { name: string }[]): boolean {
  const names = rows.map((r) => r.name.trim().toLowerCase()).filter((n) => n.length > 0);
  if (names.length !== rows.length) return false;
  return new Set(names).size === names.length;
}
